/**
 * @fileoverview Ask the organization for a substitute (docs/substitute-requests.md).
 *
 * The org decides the terms per program — how much notice, how many requests
 * before the instructor should talk to someone instead, whether a lesson plan
 * must go with it — and the server has already worked out which sessions can
 * still be requested (`requestable`). This asks only for what the org wants:
 * which sessions, a reason, and the plan when it is required.
 *
 * **When a request is not possible it says who to talk to, not just "no".**
 * Inside the notice period, or over the limit, the instructor is shown the
 * program's contacts: the admin can still assign cover by hand from the org
 * portal. Several sessions go in one request, each counted and approved on
 * its own.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { Button } from "@/components/studio/primitives";
import { leadTimeLabel, requestError, requestsLeft } from "@/lib/substitutes";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

function clock(time) {
  if (!time) return "";
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return time;
  return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function dateLabel(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y) return ymd || "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** Sessions still ahead that nobody has asked about or covered. */
const openAhead = (s) => s.upcoming && s.status !== "cancelled" && !s.covered && !s.substitute_request;

/** Who to talk to when a request can't be made, with a way to reach them. */
function Contacts({ contacts }) {
  if (!contacts?.length) {
    return (
      <p className="mt-3 font-sans text-[14px] text-[var(--text-secondary)]">
        Contact your program administrator directly.
      </p>
    );
  }
  return (
    <ul className="mt-3 divide-y divide-[var(--studio-divider)] rounded-[12px] border border-[var(--studio-divider)]" data-testid="request-sub-contacts">
      {contacts.map((c, i) => (
        <li key={`${c.email || c.name}-${i}`} className="px-4 py-3">
          <div className="font-sans text-[14px] font-medium text-[var(--text-primary)]">
            {c.name}
            {c.role && <span className="font-normal text-[var(--text-tertiary)]"> · {c.role}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-4 font-sans text-[13px]">
            {c.email && (
              <a href={`mailto:${c.email}`} className="text-[var(--accent-yellow-text)] hover:underline">
                {c.email}
              </a>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} className="text-[var(--accent-yellow-text)] hover:underline">
                {c.phone}
              </a>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * @param {{
 *   open: boolean, onOpenChange: (open: boolean) => void,
 *   classId: string, className?: string, sessions: Array, policy: object,
 *   initialOccurrenceId?: string|null, onRequested?: () => void,
 * }} props
 */
export default function RequestSubDialog({ open, onOpenChange, classId, className, sessions = [], policy, initialOccurrenceId, onRequested }) {
  const candidates = useMemo(
    () => sessions.filter((s) => s.requestable).sort((a, b) => String(a.start_at || a.date).localeCompare(String(b.start_at || b.date))),
    [sessions],
  );
  const tooClose = useMemo(() => sessions.filter((s) => openAhead(s) && !s.requestable), [sessions]);
  const left = requestsLeft(policy);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [plan, setPlan] = useState("");
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(null); // {code, message}

  useEffect(() => {
    if (!open) return;
    setReason("");
    setPlan("");
    setSaving(false);
    setBlocked(null);
    const start = candidates.find((s) => s.occurrence_id === initialOccurrenceId);
    setSelected(start ? [start.occurrence_id] : candidates.length === 1 ? [candidates[0].occurrence_id] : []);
  }, [open, initialOccurrenceId, candidates]);

  const initialTooClose = initialOccurrenceId && tooClose.some((s) => s.occurrence_id === initialOccurrenceId);
  const overLimit = left !== null && left <= 0;
  const reason409 = blocked?.code;
  const showContacts = blocked || overLimit || candidates.length === 0 || initialTooClose;
  const planMissing = policy?.lesson_plan_required && !plan.trim();
  const overSelection = left !== null && selected.length > left;

  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/classes/${classId}/substitute-requests`, {
        occurrence_ids: selected,
        reason: reason.trim() || null,
        lesson_plan: plan.trim() || null,
      });
      toast.success(
        selected.length === 1
          ? "Sub requested — your organization has been emailed"
          : `${selected.length} sessions requested — your organization has been emailed`,
      );
      onRequested?.();
      onOpenChange(false);
    } catch (err) {
      const detail = requestError(err);
      if (["too_late", "limit_reached"].includes(detail.code)) setBlocked(detail);
      else toast.error(detail.message || "Couldn't send the request");
    } finally {
      setSaving(false);
    }
  };

  const why = overLimit || reason409 === "limit_reached"
    ? `You've used all ${policy?.max_requests} of your substitute requests for this program.`
    : reason409 === "too_late" || initialTooClose || candidates.length === 0
      ? `Requests need to arrive at least ${leadTimeLabel(policy?.min_lead_hours).toLowerCase()} before the session starts.`
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="request-sub-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Request a substitute</DialogTitle>
        </DialogHeader>
        {className && <p className="font-sans text-[14px] text-[var(--text-secondary)]">{className}</p>}

        {showContacts ? (
          <div data-testid="request-sub-contact">
            <p className="mt-3 font-sans text-[14px] text-[var(--text-primary)]">{why || blocked?.message}</p>
            <p className="mt-1 font-sans text-[14px] text-[var(--text-secondary)]">
              Contact your program administrator directly — they can still arrange cover and assign a substitute
              for you.
            </p>
            <Contacts contacts={policy?.contacts} />
            <div className="mt-5">
              <Button variant="ghost" onPress={() => onOpenChange(false)} data-testid="request-sub-close">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-3 font-sans text-[13px] font-semibold text-[var(--text-tertiary)]">Sessions</div>
            <ul className="mt-2 max-h-[240px] overflow-y-auto rounded-[12px] border border-[var(--studio-divider)]" data-testid="request-sub-sessions">
              {candidates.map((s, i) => {
                const on = selected.includes(s.occurrence_id);
                return (
                  <li key={s.occurrence_id} className={i < candidates.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}>
                    <label className="flex cursor-pointer items-center gap-3 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(s.occurrence_id)}
                        className="h-4 w-4 accent-[var(--accent-yellow)]"
                        data-testid={`request-sub-pick-${s.occurrence_id}`}
                      />
                      <span className="flex-1 font-sans text-[14px] text-[var(--text-primary)]">{dateLabel(s.date)}</span>
                      <span className="font-mono text-[13px] text-[var(--text-secondary)]">{clock(s.time_local)}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {tooClose.length > 0 && (
              <p className="mt-2 font-sans text-[12px] text-[var(--text-tertiary)]" data-testid="request-sub-too-close">
                {tooClose.length === 1 ? "One session is" : `${tooClose.length} sessions are`} less than{" "}
                {leadTimeLabel(policy?.min_lead_hours).toLowerCase()} away — contact your program administrator for{" "}
                {tooClose.length === 1 ? "it" : "those"}.
              </p>
            )}
            {left !== null && (
              <p className={`mt-2 font-sans text-[12px] ${overSelection ? "text-[var(--state-danger)]" : "text-[var(--text-tertiary)]"}`} data-testid="request-sub-left">
                {overSelection
                  ? `You can request ${left} more ${left === 1 ? "session" : "sessions"} in this program.`
                  : `${policy.requests_used || 0} of ${policy.max_requests} requests used in this program.`}
              </p>
            )}

            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={2}
              maxLength={1000}
              aria-label="Reason"
              data-testid="request-sub-reason"
              className={`${inputClass} mt-4`}
            />
            {policy?.lesson_plan_required ? (
              <>
                <textarea
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  placeholder="Lesson plan for the substitute (required)"
                  rows={5}
                  maxLength={10000}
                  aria-label="Lesson plan"
                  data-testid="request-sub-plan"
                  className={`${inputClass} mt-3`}
                />
                <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                  Your organization requires a plan. The substitute reads it before the session.
                </p>
              </>
            ) : (
              <textarea
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                placeholder="Lesson plan for the substitute (optional)"
                rows={3}
                maxLength={10000}
                aria-label="Lesson plan"
                data-testid="request-sub-plan"
                className={`${inputClass} mt-3`}
              />
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button
                onPress={submit}
                disabled={saving || selected.length === 0 || planMissing || overSelection}
                data-testid="request-sub-submit"
              >
                {saving ? "Sending…" : selected.length > 1 ? `Request ${selected.length} sessions` : "Request sub"}
              </Button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
              >
                Cancel
              </button>
            </div>
            <p className="mt-3 font-sans text-[12px] text-[var(--text-tertiary)]">
              Your organization approves each session and tells you who&rsquo;s covering. Until then, the session is
              still yours.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
